import React, { useState, useEffect } from 'react';
import {
  Button,
  DatePicker,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import {
  RollbackOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { PendingItem } from '../../../services/batchApprovalApi';
import { useBatchWriteoff } from '../hooks/useBatchWriteoff';
import { buildRowKey } from '../hooks/useBatchApproval';
import BatchResultModal from './BatchResultModal';
import DocumentDetailModal from './DocumentDetailModal';

const { RangePicker } = DatePicker;
const { Text } = Typography;

// ============================================================
// Status helper
// ============================================================

function statusTag(status: string): React.ReactNode {
  switch (status) {
    case 'S':
    case 'D': // FC120（交運單）法定送審值為 'D'
      return <Tag color="orange">待審核</Tag>;
    case 'P':
      return <Tag color="green">已核准</Tag>;
    case 'R':
      return <Tag color="red">已退回</Tag>;
    default:
      return <Tag>{status || '—'}</Tag>;
  }
}

// ============================================================
// Component
// ============================================================

const WriteoffTab: React.FC = () => {
  const {
    loading,
    operating,
    items,
    total,
    modules,
    selectedKeys,
    filter,
    resultData,
    resultVisible,
    setFilter,
    setSelectedKeys,
    fetchApproved,
    batchWriteoff,
    closeResult,
  } = useBatchWriteoff();

  const [detailItem, setDetailItem] = useState<PendingItem | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [writeoffComment, setWriteoffComment] = useState('');

  // Auto-load on mount
  useEffect(() => {
    fetchApproved();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // Table columns
  // ============================================================

  const columns: ColumnsType<PendingItem> = [
    {
      title: '模組名稱',
      dataIndex: 'modName',
      key: 'modName',
      width: 140,
      ellipsis: true,
    },
    {
      title: '單據號碼',
      dataIndex: 'recordNo',
      key: 'recordNo',
      width: 130,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: '日期',
      dataIndex: 'recordDate',
      key: 'recordDate',
      width: 110,
      render: (date: string | null) =>
        date ? new Date(date).toLocaleDateString('zh-TW') : '—',
    },
    {
      title: '建立人',
      dataIndex: 'creator',
      key: 'creator',
      width: 100,
    },
    {
      title: '狀態',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      align: 'center',
      render: statusTag,
    },
  ];

  // ============================================================
  // Row selection
  // ============================================================

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  // ============================================================
  // Filter handlers
  // ============================================================

  const handleModuleChange = (value: string) => {
    setFilter({ modId: value });
  };

  const handleDateChange = (_: unknown, dateStrings: [string, string]) => {
    setFilter({ dateFrom: dateStrings[0], dateTo: dateStrings[1] });
  };

  const handleSearch = () => {
    fetchApproved();
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilter({ page, size: pageSize });
  };

  // Refetch when filter changes (module, date, page)
  useEffect(() => {
    fetchApproved();
  }, [filter.modId, filter.dateFrom, filter.dateTo, filter.page]); // eslint-disable-line react-hooks/exhaustive-deps

  // ============================================================
  // Row click
  // ============================================================

  const handleRowClick = (record: PendingItem) => {
    setDetailItem(record);
    setDetailVisible(true);
  };

  // ============================================================
  // Render
  // ============================================================

  return (
    <div>
      {/* Filter Bar */}
      <div className="batch-filter-bar">
        <Space wrap>
          <Select
            value={filter.modId || undefined}
            onChange={handleModuleChange}
            placeholder="全部模組"
            allowClear
            style={{ width: 180 }}
            options={modules.map((m) => ({
              value: m.modId,
              label: m.displayName,
            }))}
          />
          <RangePicker
            value={[
              filter.dateFrom ? dayjs(filter.dateFrom) : null,
              filter.dateTo ? dayjs(filter.dateTo) : null,
            ]}
            onChange={handleDateChange as any}
            format="YYYY-MM-DD"
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleSearch}
            loading={loading}
          >
            查詢
          </Button>
        </Space>
      </div>

      {/* Data Table */}
      <Table<PendingItem>
        rowKey={buildRowKey}
        columns={columns}
        dataSource={items}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          current: filter.page,
          pageSize: filter.size,
          total,
          onChange: handlePageChange,
          showTotal: (t) => `共 ${t} 筆`,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
        }}
        scroll={{ x: 600 }}
        locale={{ emptyText: '無待沖銷資料' }}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
        })}
      />

      {/* Bottom action bar */}
      <div className="batch-action-bar">
        <Space>
          <Text type="secondary">
            已選 <Text strong>{selectedKeys.length}</Text> 筆
          </Text>
        </Space>
        <Space>
          <Popconfirm
            title={`確認沖銷 ${selectedKeys.length} 筆文件？`}
            description={
              <Input.TextArea
                rows={2}
                placeholder="沖銷備註（選填）"
                value={writeoffComment}
                onChange={(e) => setWriteoffComment(e.target.value)}
              />
            }
            onConfirm={() => {
              batchWriteoff(writeoffComment);
              setWriteoffComment('');
            }}
            okText="確認沖銷"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            disabled={selectedKeys.length === 0}
          >
            <Button
              type="primary"
              danger
              icon={<RollbackOutlined />}
              loading={operating}
              disabled={selectedKeys.length === 0}
            >
              全部沖銷
            </Button>
          </Popconfirm>
        </Space>
      </div>

      {/* Result Modal */}
      <BatchResultModal
        visible={resultVisible}
        data={resultData}
        onClose={closeResult}
        title="批次沖銷結果"
      />

      {/* Document Detail Modal */}
      <DocumentDetailModal
        visible={detailVisible}
        item={detailItem}
        onClose={() => setDetailVisible(false)}
      />
    </div>
  );
};

export default WriteoffTab;
